import { defineStore } from 'pinia';
import {
	ACTIVE_RIDE_STATUSES,
	FINAL_RIDE_STATUSES,
	RideStatus,
	type GeoPoint,
	type LatLng,
	type Place,
	type ResultList,
	type Ride,
} from '@taxi/shared';

export interface RideEstimate {
	distanceMeters: number | null;
	durationSeconds: number | null;
	fareMin: number | null;
	fareMax: number | null;
	availableDrivers: number;
	nearestEtaSeconds: number | null;
}

export interface NearbyDriver {
	position: LatLng;
	heading: number | null;
	distanceMeters: number;
	etaSeconds: number;
	category: string | null;
}

export interface RidePointInput {
	label?: string;
	placeId?: number | null;
	lat?: number | null;
	lng?: number | null;
	details?: string | null;
}

export interface RideState {
	current: Ride | null;
	history: Ride[];
	places: Place[];
	nearby: NearbyDriver[];
	estimate: RideEstimate | null;
	/** Position du chauffeur, rafraîchie en direct pendant l'approche. */
	driverPosition: GeoPoint | null;
	pickup: RidePointInput | null;
	dropoff: RidePointInput | null;
	passengers: number;
	luggage: number;
	note: string;
	/**
	 * Heure de prise en charge souhaitée, en ISO.
	 *
	 * `null` pour un départ immédiat. Portée par le parcours de commande et non
	 * par l'écran : le choix se fait sur l'écran de confirmation, mais il doit
	 * survivre à un aller-retour vers la destination.
	 */
	scheduledAt: string | null;
	loading: boolean;
}

/**
 * Course en cours et parcours de commande.
 *
 * Source de vérité côté client : les événements temps réel comme le repli par
 * interrogation aboutissent tous les deux ici, si bien que les écrans n'ont pas
 * à savoir par quel canal l'information est arrivée.
 */
export const useRideStore = defineStore('ride', {
	state: (): RideState => ({
		current: null,
		history: [],
		places: [],
		nearby: [],
		estimate: null,
		driverPosition: null,
		pickup: null,
		dropoff: null,
		passengers: 1,
		luggage: 0,
		note: '',
		scheduledAt: null,
		loading: false,
	}),

	getters: {
		hasActiveRide: (state): boolean =>
			!!state.current && ACTIVE_RIDE_STATUSES.includes(state.current.status),

		isSearching: (state): boolean => state.current?.status === RideStatus.SEARCHING,

		isFinished: (state): boolean =>
			!!state.current && FINAL_RIDE_STATUSES.includes(state.current.status),

		/** Lieux mis en avant sur l'écran d'accueil de la borne. */
		featuredPlaces: (state): Place[] => state.places.filter((place) => place.featured),

		placesByType:
			(state) =>
			(type: string): Place[] =>
				state.places.filter((place) => place.type === type),

		/** Position à suivre sur la carte : le taxi s'il est assigné, sinon rien. */
		trackedPosition: (state): GeoPoint | null =>
			state.driverPosition ?? state.current?.driver?.position ?? null,
	},

	actions: {
		// -- Catalogue ---------------------------------------------------------

		async loadPlaces(origin?: LatLng | null): Promise<Place[]> {
			const query = origin ? `?lat=${origin.lat}&lng=${origin.lng}` : '';
			this.places = await this.$caller().get<Place[]>(`/places${query}`);
			return this.places;
		},

		// -- Recherche ---------------------------------------------------------

		/** Taxis disponibles autour d'un point, pour la carte de l'écran de commande. */
		async loadNearby(origin: LatLng): Promise<NearbyDriver[]> {
			this.nearby = await this.$caller().get<NearbyDriver[]>(
				`/drivers/nearby?lat=${origin.lat}&lng=${origin.lng}`,
				{ silentError: true, abortKey: 'nearby' },
			);
			return this.nearby;
		},

		async runEstimate(): Promise<RideEstimate | null> {
			if (!this.pickup) {
				return null;
			}
			this.estimate = await this.$caller().post<RideEstimate>(
				'/rides/estimate',
				{ pickup: this.pickup, dropoff: this.dropoff ?? undefined },
				{ abortKey: 'estimate' },
			);
			return this.estimate;
		},

		// -- Commande ----------------------------------------------------------

		setPickup(point: RidePointInput): void {
			this.pickup = point;
		},

		setDropoff(point: RidePointInput | null): void {
			this.dropoff = point;
		},

		async book(guest?: { name?: string; phone?: string }): Promise<Ride> {
			this.loading = true;
			try {
				const ride = await this.$caller().post<Ride>('/rides', {
					pickup: this.pickup,
					dropoff: this.dropoff ?? undefined,
					passengers: this.passengers,
					luggage: this.luggage,
					note: this.note || undefined,
					scheduledAt: this.scheduledAt ?? undefined,
					guestName: guest?.name,
					guestPhone: guest?.phone,
				});
				this.current = ride;
				this.driverPosition = null;
				return ride;
			} finally {
				this.loading = false;
			}
		},

		async cancel(reason?: string): Promise<void> {
			if (!this.current) {
				return;
			}
			this.current = await this.$caller().post<Ride>(`/rides/${this.current.id}/cancel`, {
				reason,
			});
		},

		// -- Suivi -------------------------------------------------------------

		/** Recharge la course active — appelé à l'ouverture et à chaque interrogation. */
		async loadCurrent(): Promise<Ride | null> {
			this.current = await this.$caller().get<Ride | null>('/rides/current', {
				silentError: true,
			});
			return this.current;
		},

		async loadRide(rideId: number): Promise<Ride> {
			const ride = await this.$caller().get<Ride>(`/rides/${rideId}`);
			this.current = ride;
			return ride;
		},

		async loadHistory(page = 1): Promise<ResultList<Ride>> {
			const result = await this.$caller().get<ResultList<Ride>>(
				`/rides?page=${page}&itemsPerPage=20`,
			);
			this.history = page === 1 ? result.items : [...this.history, ...result.items];
			return result;
		},

		/**
		 * Ouvre le suivi depuis le QR code de la borne, sans compte.
		 *
		 * Ne consomme pas le jeton : le voyageur peut consulter puis s'inscrire, la
		 * course étant alors rattachée à son compte tout neuf.
		 */
		async track(claimToken: string): Promise<Ride> {
			const ride = await this.$caller().post<Ride>('/rides/track', { claimToken });
			this.current = ride;
			return ride;
		},

		/** Rattache définitivement la course au compte connecté. */
		async claim(claimToken: string): Promise<Ride> {
			const ride = await this.$caller().post<Ride>('/rides/claim', { claimToken });
			this.current = ride;
			return ride;
		},

		// -- Réception des mises à jour ---------------------------------------

		/** Applique une mise à jour reçue en temps réel. */
		applyRideUpdate(ride: Ride): void {
			if (!this.current || this.current.id === ride.id) {
				this.current = ride;
			}
			const index = this.history.findIndex((item) => item.id === ride.id);
			if (index !== -1) {
				this.history.splice(index, 1, ride);
			}
		},

		applyDriverPosition(rideId: number, position: GeoPoint): void {
			if (this.current?.id === rideId) {
				this.driverPosition = position;
			}
		},

		/** Remet le parcours de commande à zéro (retour à l'accueil de la borne). */
		reset(): void {
			this.current = null;
			this.estimate = null;
			this.driverPosition = null;
			this.pickup = null;
			this.dropoff = null;
			this.passengers = 1;
			this.luggage = 0;
			this.note = '';
			this.scheduledAt = null;
		},
	},
});
